import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { useCaptchaFallback } from '@/Domain/hooks/Auth/useCaptchaFallback.hook';
import type { Error } from '@/Domain/utils/errorMapper';

const createForgotPasswordValidator = () =>
  z.object({
    email: z.string().email(i18n.t('validation:invalid_email')),
    recaptcha_response_v3: z.string().optional(),
    recaptcha_response_v2: z.string().optional(),
  });

export type ForgotPasswordForm = z.infer<ReturnType<typeof createForgotPasswordValidator>>;

export const useForgotPasswordHook = () => {
  useTranslation('validation');
  const { track } = useAnalytics();

  const {
    control,
    getValues,
    handleSubmit,
    register,
    clearErrors,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(createForgotPasswordValidator()),
  });

  register('recaptcha_response_v3');

  const {
    handleCaptchaError,
    handleSubmissionError,
    escalateToV2,
    handleV2Focus,
    isPreparingSubmission,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  } = useCaptchaFallback({
    clearSubmissionErrors: () => {
      clearErrors('root');
      clearErrors('recaptcha_response_v2');
    },
    hasV2Token: () => !!getValues('recaptcha_response_v2'),
    resetV2Token: () => setValue('recaptcha_response_v2', ''),
    setGenericError: () => setError('root', { message: i18n.t('forgot_password:error_generic') }),
    setRequiredV2Error: () => setError('recaptcha_response_v2', { type: 'required' }),
  });

  const forgot = useMutation<unknown, Error<ForgotPasswordForm>, ForgotPasswordForm>({
    mutationKey: [QueryKey.accounts.forgotPassword],
    onSuccess: () => {
      track('forgot_password_form_success', {
        event_category: 'Forgot password',
        event_label: 'forgot_password_form',
      });
    },
    onError: (error) => {
      handleSubmissionError();
      if ((error.response?.status ?? 0) >= 500) {
        setError('root', { message: i18n.t('forgot_password:error_generic') });
      } else if (error.response?.data?.errors?.recaptcha_response_v3) {
        handleCaptchaError('recaptcha_response_v3');
      } else if (error.response?.data?.errors?.recaptcha_response_v2) {
        handleCaptchaError('recaptcha_response_v2');
      } else {
        setError('root', { message: i18n.t('forgot_password:error_generic') });
      }

      track('forgot_password_form_error', {
        event_category: 'Forgot password',
        event_label: 'forgot_password_form',
      });
    },
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    const payload = await prepareCaptchaSubmission(data);
    if (!payload) {
      return;
    }

    track('forgot_password_form_submit', {
      event_category: 'Forgot password',
      event_label: 'forgot_password_form',
    });

    forgot.mutate(payload);
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [setValue],
  );

  const resetRequest = () => {
    reset({
      email: getValues('email'),
      recaptcha_response_v3: '',
      recaptcha_response_v2: '',
    });
    forgot.reset();
  };

  return {
    register,
    errors,
    escalateToV2,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    forgot,
    control,
    handleSubmit,
    onSubmit,
    onVerify,
    resetRequest,
    isPending: forgot.isPending || isPreparingSubmission,
  };
};
